import * as Sentry from '@sentry/react';

import { env } from './env';

/** A breadcrumb path segment matching one of these is a photo storage key, never a route. */
const IMAGE_EXTENSION_RE = /\.(jpe?g|png|webp|heic|heif)$/i;

/**
 * scrubBreadcrumbUrl (LOW fix, adversarial review) — strips the query
 * string (removes EVERY presigned-URL auth param — S3's own
 * `X-Amz-Signature`/`X-Amz-Credential`, or any other provider's
 * equivalent, without needing to know which host issued the URL: this
 * codebase's OWN API calls never carry sensitive data in a query string,
 * only in headers — see `client.ts` — so blanket-stripping it is safe) and
 * redacts the last path segment when it looks like a photo storage key
 * (an image-extension filename — our own backend's routes never end in
 * one, so this only ever touches a photo upload/download URL).
 * Exported for direct unit testing.
 */
export function scrubBreadcrumbUrl(rawUrl: string): string {
  let pathname: string;
  let origin: string;
  try {
    const parsed = new URL(rawUrl, 'http://placeholder.invalid');
    pathname = parsed.pathname;
    origin = parsed.origin === 'http://placeholder.invalid' ? '' : parsed.origin;
  } catch {
    // Not a parseable absolute/relative URL — fall back to a plain string
    // split so the query string is still removed even here.
    const [path] = rawUrl.split('?');
    return path ?? rawUrl;
  }

  const segments = pathname.split('/');
  const last = segments[segments.length - 1];
  if (last && IMAGE_EXTENSION_RE.test(last)) {
    segments[segments.length - 1] = '<photo>';
  }
  return `${origin}${segments.join('/')}`;
}

/**
 * scrubNavigationBreadcrumb (LOW fix, adversarial review) — Sentry's
 * automatic browser navigation instrumentation records the route's full
 * `to`/`from` path INCLUDING its query string (e.g. the search page's own
 * `?q=<plate or phone>` — a genuine PII leak this codebase's own "no PII,
 * ever" call sites, `observability.ts`, never had a chance to prevent,
 * exactly like the fetch/xhr case below). Same blanket "strip the query
 * string" policy — this app never needs one in a Sentry breadcrumb.
 * Returns the SAME object when neither field carries a query string (no
 * unnecessary breadcrumb churn).
 */
function scrubNavigationBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  let changed = false;
  const next: Record<string, unknown> = { ...data };
  for (const key of ['to', 'from'] as const) {
    const value = data[key];
    if (typeof value === 'string' && value.includes('?')) {
      next[key] = value.split('?')[0];
      changed = true;
    }
  }
  return changed ? { ...breadcrumb, data: next } : breadcrumb;
}

/**
 * scrubBreadcrumb — Sentry's `beforeBreadcrumb` hook. Touches `fetch`/`xhr`
 * category breadcrumbs (Sentry's own automatic network instrumentation)
 * with a string `data.url`, and `navigation` breadcrumbs (Sentry's own
 * automatic route-change instrumentation) — the TWO observability surfaces
 * this codebase does not directly author, unlike `observability.ts`'s own
 * hand-written "no PII, ever" calls. Every other breadcrumb (clicks,
 * console, the app's own `recordSyncOutcome` etc.) passes through
 * unchanged. Exported for direct unit testing.
 */
export function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category === 'navigation') return scrubNavigationBreadcrumb(breadcrumb);
  if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'xhr') return breadcrumb;
  const url = breadcrumb.data?.url;
  if (typeof url !== 'string') return breadcrumb;
  return { ...breadcrumb, data: { ...breadcrumb.data, url: scrubBreadcrumbUrl(url) } };
}

export function initSentry(): void {
  if (!env.sentryDsn) return;

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.sentryEnv,
    // Atribuye cada error a un deploy concreto. Se OMITE cuando la version se
    // desconoce: un release inventado simula trazabilidad y estorba justo en
    // medio de un incidente. Ver env.ts.
    ...(env.appVersion ? { release: env.appVersion } : {}),
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // LOW fix (adversarial review) — see scrubBreadcrumb's own doc comment.
    beforeBreadcrumb: scrubBreadcrumb,
  });

  // pwa-offline-first Slice 7, task 7.9 (design §9) — `sw.version` tag. The
  // service worker deploys atomically with the app build (vite-plugin-pwa
  // injectManifest, no separate SW-internal version string), so the app's
  // own build version IS the SW's version. Omitted when appVersion is
  // unset — same "never invent a release" posture as above.
  if (env.appVersion) {
    Sentry.setTag('sw.version', env.appVersion);
  }
}
